// src/pages/manager/ManagerPartnersPage.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { auth, db, functions } from '../../firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import EditPartnerModal from '../../EditPartnerModal';
import AddPartnerForm from '../../AddPartnerForm';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    IconButton, CircularProgress, Tooltip, Typography, Box, Link,
    TextField, Select, MenuItem, FormControl, InputLabel, Button,
    Snackbar, Alert
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

const ZONAS = [
    "Aclimação", "Carrão", "Centro (São Paulo)", "Diadema", "Guarulhos", "Ipiranga",
    "Itaquera", "Litoral", "Mooca", "Morumbi", "Saúde", "Santo André",
    "São Bernardo do Campo", "São Caetano do Sul"
];

const ITEMS_PER_PAGE = 50;

function ManagerPartnersPage() {
    const user = auth.currentUser;
    const [allParceiros, setAllParceiros] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingPartner, setEditingPartner] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [filters, setFilters] = useState({ busca: '', zona: '', status: '' });
    const [page, setPage] = useState(1);

    // Ao mudar qualquer filtro, volta para a página 1
    const handleFilterChange = (e) => {
        setFilters(prev => ({ ...prev, [e.target.name]: e.target.value }));
        setPage(1);
    };

    // Carrega TODOS os parceiros do gerente de uma vez e ordena alfabeticamente
    const fetchParceiros = useCallback(async () => {
        if (!user) return;
        setLoading(true);
        try {
            const q = query(collection(db, "usuarios"), where("gerenteId", "==", user.uid));
            const data = await getDocs(q);
            const lista = data.docs.map(doc => ({ ...doc.data(), id: doc.id }));
            // Ordenação alfabética por nome com suporte a caracteres pt-BR
            lista.sort((a, b) => (a.nome || '').localeCompare(b.nome || '', 'pt-BR'));
            setAllParceiros(lista);
        } catch (error) {
            console.error("Erro ao buscar parceiros do gerente:", error);
        }
        setLoading(false);
    }, [user]);

    useEffect(() => { fetchParceiros(); }, [fetchParceiros]);

    // Filtragem client-side: busca global (nome + email + telefone), zona e status
    // Case-insensitive e sem sensibilidade a acentos via normalize
    const filteredParceiros = useMemo(() => {
        return allParceiros.filter(p => {
            const busca = filters.busca.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
            const nome    = (p.nome || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
            const email   = (p.email || '').toLowerCase();
            const telefone = (p.telefone || '').replace(/\D/g, '');
            const buscaDigits = filters.busca.replace(/\D/g, '');
            const buscaOk = !filters.busca || nome.includes(busca) || email.includes(busca) || (buscaDigits.length > 0 && telefone.includes(buscaDigits));
            const zonaOk  = !filters.zona || (p.zonaAtuacao || []).includes(filters.zona);
            const statusOk = !filters.status || p.status === filters.status;
            return buscaOk && zonaOk && statusOk;
        });
    }, [allParceiros, filters]);

    // Paginação client-side
    const totalPages = Math.max(1, Math.ceil(filteredParceiros.length / ITEMS_PER_PAGE));
    const paginatedParceiros = useMemo(() => {
        const start = (page - 1) * ITEMS_PER_PAGE;
        return filteredParceiros.slice(start, start + ITEMS_PER_PAGE);
    }, [filteredParceiros, page]);

    const handleEdit = (partner) => {
        setEditingPartner(partner);
        setIsModalOpen(true);
    };

    const handleDelete = async (partner) => {
        const confirmado = window.confirm(
            `Tem certeza que deseja excluir "${partner.nome}"?\n\nEssa ação será registrada no log de auditoria e não pode ser desfeita.`
        );
        if (!confirmado) return;
        try {
            const deletePartnerFn = httpsCallable(functions, 'deletePartner');
            await deletePartnerFn({ partnerId: partner.id });
            setSnackbar({ open: true, message: `Parceiro "${partner.nome}" excluído com sucesso.`, severity: 'success' });
            fetchParceiros();
        } catch (err) {
            console.error("Erro ao excluir parceiro:", err);
            setSnackbar({ open: true, message: 'Erro ao excluir parceiro: ' + err.message, severity: 'error' });
        }
    };

    const handleLimparFiltros = () => {
        setFilters({ busca: '', zona: '', status: '' });
        setPage(1);
    };

    return (
        <>
            <AddPartnerForm onPartnerAdded={fetchParceiros} />

            {/* Barra de filtros */}
            <Paper sx={{ p: 2, my: 4, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                    name="busca"
                    label="Buscar por nome, e-mail ou telefone..."
                    size="small"
                    sx={{ minWidth: 280 }}
                    value={filters.busca}
                    onChange={handleFilterChange}
                />
                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Filtrar por Zona</InputLabel>
                    <Select name="zona" label="Filtrar por Zona" value={filters.zona} onChange={handleFilterChange}>
                        <MenuItem value=""><em>Todas</em></MenuItem>
                        {ZONAS.map(z => <MenuItem key={z} value={z}>{z}</MenuItem>)}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Status</InputLabel>
                    <Select name="status" label="Status" value={filters.status} onChange={handleFilterChange}>
                        <MenuItem value=""><em>Todos</em></MenuItem>
                        <MenuItem value="ativo">Ativo</MenuItem>
                        <MenuItem value="inativo">Inativo</MenuItem>
                    </Select>
                </FormControl>
                <Button variant="contained" onClick={fetchParceiros}>Buscar</Button>
                <Button variant="outlined" onClick={handleLimparFiltros}>Limpar Filtros</Button>
            </Paper>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Nome</TableCell>
                            <TableCell>E-mail</TableCell>
                            <TableCell>WhatsApp</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={5} align="center"><CircularProgress /></TableCell></TableRow>
                        ) : paginatedParceiros.length === 0 ? (
                            <TableRow><TableCell colSpan={5} align="center">Nenhum parceiro encontrado para os filtros aplicados.</TableCell></TableRow>
                        ) : (
                            paginatedParceiros.map(p => {
                                const wppUrl = p.telefone ? `https://wa.me/55${p.telefone.replace(/\D/g, '')}` : null;
                                return (
                                    <TableRow key={p.id}>
                                        <TableCell>{p.nome}</TableCell>
                                        <TableCell>{p.email}</TableCell>
                                        <TableCell>
                                            {wppUrl ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <Tooltip title="Abrir no WhatsApp">
                                                        <IconButton component="a" href={wppUrl} target="_blank" color="success" size="small">
                                                            <WhatsAppIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Link href={wppUrl} target="_blank" underline="hover" color="inherit" sx={{ ml: 1, fontSize: '0.875rem' }}>
                                                        {p.telefone}
                                                    </Link>
                                                </Box>
                                            ) : (p.telefone || '-')}
                                        </TableCell>
                                        <TableCell>{p.status}</TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Editar">
                                                <IconButton onClick={() => handleEdit(p)} color="primary"><EditIcon /></IconButton>
                                            </Tooltip>
                                            <Tooltip title="Excluir">
                                                <IconButton onClick={() => handleDelete(p)} color="error"><DeleteIcon /></IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Rodapé de paginação */}
            {!loading && filteredParceiros.length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, px: 1 }}>
                    <Button size="small" variant="outlined" onClick={() => setPage(p => p - 1)} disabled={page === 1}>
                        ← Anterior
                    </Button>
                    <Typography variant="body2" color="text.secondary">
                        Página {page} de {totalPages} · {filteredParceiros.length} parceiros
                    </Typography>
                    <Button size="small" variant="outlined" onClick={() => setPage(p => p + 1)} disabled={page === totalPages}>
                        Próxima →
                    </Button>
                </Box>
            )}

            {isModalOpen && (
                <EditPartnerModal
                    user={user}
                    partner={editingPartner}
                    onClose={() => setIsModalOpen(false)}
                    onPartnerUpdated={() => { setIsModalOpen(false); fetchParceiros(); }}
                />
            )}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
}

export default ManagerPartnersPage;
